import type { Kind } from "@norkron/web-review-workbench";
import type { ArticleResource } from "../../api/articleUnits";
import { Check, Eye, Loader2, PenLine, Pin, Repeat2 } from "../../compose/Icon";
import { roleTitle } from "../../lib/workbenchLayout";

/**
 * `<ResourceHeader>` — a resource pane's title row (design §2.3): the role label,
 * the `v{seq} · hash8` version chip, a stale pin (when the resource lags a
 * section-link pin, design §1.6), a read/edit toggle, a `Save` control (edit
 * mode, W2), and a `Compare…` control. Pure presentational — every interactive
 * element carries a `data-testid`.
 *
 * The version chip reflects the version the pane is SHOWING, supplied via
 * `versionSeq`/`versionHash`: by default the head (the aggregate `resource`
 * carries only a placeholder seq of 1 — the accurate head seq comes from the
 * timeline ticks the workbench passes in), or a past snapshot the reviewer opened
 * from the timeline. While a past snapshot is shown (`viewingPast`) the pane is
 * read-only: the chip wears a `read-only · return to current` control
 * (→ `onReturnToHead`) and Edit/Save are withheld until the reviewer returns to
 * the head.
 */
export interface ResourceHeaderProps {
  readonly resource: ArticleResource;
  readonly editable: boolean;
  /** brief/article need a WASM DoczHandle to edit (W2 supplies it). */
  readonly canEdit: boolean;
  readonly onToggleEdit: () => void;
  readonly onCompare: () => void;
  /** W2: commit the pane's edits (whole-resource save-back). Absent → no Save. */
  readonly onSave?: () => void;
  /** W2: the pane has unsaved edits (enables Save). */
  readonly dirty?: boolean;
  /** W2: a save is in flight (spinner + disables Save). */
  readonly saving?: boolean;
  /** The seq of the version the pane is SHOWING (defaults to `resource.seq`, a
   *  placeholder — pass the head/selected seq so the chip reads the real version). */
  readonly versionSeq?: number;
  /** The content hash of the version the pane is SHOWING (defaults to
   *  `resource.content_hash`); the chip renders its first 8 chars. */
  readonly versionHash?: string;
  /** A past (non-head) snapshot is shown → read-only: offer "return to current"
   *  and withhold Edit/Save. */
  readonly viewingPast?: boolean;
  /** Return the pane to the live head version. */
  readonly onReturnToHead?: () => void;
}

export function ResourceHeader({
  resource,
  editable,
  canEdit,
  onToggleEdit,
  onCompare,
  onSave,
  dirty = false,
  saving = false,
  versionSeq,
  versionHash,
  viewingPast = false,
  onReturnToHead,
}: ResourceHeaderProps) {
  const kind = resource.role as Kind;
  const seq = versionSeq ?? resource.seq;
  const hash = versionHash ?? resource.content_hash;
  return (
    <header className="wb-pane__head" data-testid={`resource-header-${kind}`}>
      <span className="wb-pane__label" data-kind={kind}>
        {roleTitle(kind)}
      </span>
      <span
        className={`wb-pane__version${viewingPast ? " wb-pane__version--past" : ""}`}
        data-testid={`resource-version-${kind}`}
        title={hash}
      >
        v{seq} · {hash.slice(0, 8)}
      </span>
      {viewingPast ? (
        <button
          type="button"
          className="wb-pane__version-return"
          data-testid={`resource-version-return-${kind}`}
          title="Return to the current head version"
          onClick={onReturnToHead}
        >
          <Eye size={12} strokeWidth={1.5} />
          read-only · return to current
        </button>
      ) : null}
      {resource.stale ? (
        <span
          className="wb-pane__stale"
          data-testid={`resource-stale-${kind}`}
          title="A section-link pin lags this resource's head"
        >
          <Pin size={12} strokeWidth={1.5} />
          stale
        </span>
      ) : null}
      <span className="wb-pane__head-spacer" />
      {viewingPast ? null : (
        <button
          type="button"
          className="wb-icon-btn"
          data-testid={`resource-edit-toggle-${kind}`}
          // ── IT IS AN ACTION BUTTON, SO IT CARRIES NO `aria-pressed` ──────────
          // It used to say `aria-pressed={editable}` — and it is labelled with its
          // DESTINATION, not its state. So in edit mode a screen reader was told
          // «Read, toggle button, PRESSED»: the label names read mode, the state
          // claims read mode is the active one, and the pane is in EDIT. A blind
          // reviewer was told the exact opposite of the truth (owner-measured
          // 2026-07-30, alongside the focus P0 above).
          //
          // Two coherent fixes existed. Making it a REAL toggle would mean
          // relabelling it with the STATE ("Edit mode", pressed/unpressed) — a
          // visible copy change to a shipped control, and it reads worse: the
          // label would stop saying what pressing it does.
          //
          // So: keep the action semantics the visible design already has (the
          // Play/Pause pattern — the label names the destination, activating it
          // relabels the button) and DROP the state attribute that contradicted
          // them. `aria-label` carries the full action so the accessible name is
          // unambiguous on its own, instead of leaning on a bare "Read"/"Edit"
          // that only makes sense next to the visible pane.
          aria-label={
            canEdit
              ? editable
                ? "Switch to read mode"
                : "Switch to edit mode"
              : "Editing arrives in Wave 2"
          }
          disabled={!canEdit}
          title={
            canEdit
              ? editable
                ? "Switch to read mode"
                : "Switch to edit mode"
              : "Editing arrives in Wave 2"
          }
          onClick={onToggleEdit}
        >
          {editable ? (
            <Eye size={14} strokeWidth={1.5} />
          ) : (
            <PenLine size={14} strokeWidth={1.5} />
          )}
          {editable ? "Read" : "Edit"}
        </button>
      )}
      {!viewingPast && editable && onSave ? (
        <button
          type="button"
          className="wb-icon-btn wb-icon-btn--primary"
          data-testid={`resource-save-${kind}`}
          disabled={!dirty || saving}
          aria-busy={saving}
          title={
            saving
              ? "Saving…"
              : dirty
                ? "Save this pane's edits"
                : "No unsaved changes"
          }
          onClick={onSave}
        >
          {saving ? (
            <Loader2 size={14} strokeWidth={1.5} />
          ) : (
            <Check size={14} strokeWidth={1.5} />
          )}
          {saving ? "Saving…" : "Save"}
        </button>
      ) : null}
      <button
        type="button"
        className="wb-icon-btn"
        data-testid={`resource-compare-${kind}`}
        title="Compare two versions"
        onClick={onCompare}
      >
        <Repeat2 size={14} strokeWidth={1.5} />
        Compare
      </button>
    </header>
  );
}
